/**
 * accounts domain — /accounts/* (22 live routes).
 * Source: docs/api/endpoints/accounts-*.md + scan-id-document.
 * Request/response shapes: PascalCase (legacy convention).
 */

import type {
  RequestOptions,
  CustSrchRequest,
  CustSrchResponse,
  CustAddRequest,
  CustAddResult,
  CustModRequest,
  CustInqResult,
  CustRelAddRequest,
  CustAcctRelInqResult,
  CustAcctRelInqEnrichedResult,
  IdVerifyAddRequest,
  ScanIdResult,
  AcctIdGenRequest,
  AcctIdGenResult,
  AcctAddRequest,
  AcctAddResult,
  AcctInqResult,
  AcctBalInqResult,
  AcctKillRequest,
  AcctModRequest,
  AddrSrchRequest,
  AddrSrchResult,
  AddrSaveRequest,
  SafeDepBoxSrchRequest,
  SafeDepBoxSrchResult,
  SafeDepVacateRequest,
  NetTellerEnrollRequest,
  NetTellerEnrollResult,
  NetTellerAssociateRequest,
  EftCardAddRequest,
  EftCardAddResult,
  EftCardModRequest,
  SendDocumentRequest,
  DomainResponseEnvelope,
  GenericResult,
  AccountPartiesRequest,
  AccountPartiesResponse,
  OdCustomerTotalResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

/** Body for POST /accounts/secondary-id — maps 1:1 to the CD ID2 block. */
export interface SecondaryIdWriteRequest {
  /** Customer (CIF) number. */
  CifId: string;
  /** F814 — 2nd ID Type. Same IdVerifyCode enum as F254 (1=DL, 3=Passport, ...). */
  IdType2?: string;
  /** F801 — Identification #2 (the number). */
  IdNumber2?: string;
  /** F813 — 2nd ID Issuer. */
  IdIssuer2?: string;
  /** F815 — 2nd ID Issue Date. Any common format; normalized server-side. */
  IdIssueDate2?: string;
  /** F816 — 2nd ID Exp Date. Any common format; normalized server-side. */
  IdExpiryDate2?: string;
  /** Officer code. REQUIRED — Core Director records who wrote the ID. */
  OffCode: string;
  OperatorNumber?: string;
  IsTestAccount?: boolean;
}

export interface SecondaryIdWriteResult {
  success: boolean;
  /** false + no error ⇒ there was no usable 2nd ID to write (not a failure). */
  wrote: boolean;
  reason?: string | null;
  idType2?: string | null;
}


/** BACKLOG-0873 reissue read-back — current design of an account's card. */
export interface AccountCardStyleResponse {
  success: boolean;
  found: boolean;
  /** EFTCardStyleId (101/103/104/105…) — undefined/null when no card found. */
  styleId?: string | null;
  /** EFTCardStatType (Act, WarmCard, Cls…). */
  statusType?: string | null;
  cardLast4?: string | null;
  error?: string;
}

export class AccountsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  // ---------------------------------------------------------------------------
  // CIF
  // ---------------------------------------------------------------------------

  public custSrch(req: CustSrchRequest, opts?: RequestOptions): Promise<CustSrchResponse> {
    return this.http.post<CustSrchResponse>('/accounts/cust-srch', { ...opts, body: req });
  }

  public custAdd(req: CustAddRequest, opts?: RequestOptions): Promise<CustAddResult> {
    return this.http.post<CustAddResult>('/accounts/cust-add', { ...opts, body: req });
  }

  public custMod(req: CustModRequest, opts?: RequestOptions): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/cust-mod', { ...opts, body: req });
  }

  /**
   * Write a customer's SECONDARY CIP identification — CD Fields
   * 814/801/813/815/816.
   *
   * CustMod CANNOT carry a 2nd ID (its only "2" fields are StreetAddr2 /
   * Email2), and a 2nd `idVerifyAdd` is single-slot — it OVERWRITES the primary
   * F254-258 block and destroys ID1 (Bill's repro, 2026-07-15). So a 2nd ID goes
   * through this route and only this route.
   *
   * `wrote: false` with no error means there was no usable 2nd ID to write —
   * a normal outcome, not a failure.
   */
  public secondaryIdWrite(
    req: SecondaryIdWriteRequest,
    opts?: RequestOptions
  ): Promise<DomainResponseEnvelope<SecondaryIdWriteResult>> {
    return this.http.post<DomainResponseEnvelope<SecondaryIdWriteResult>>(
      '/accounts/secondary-id',
      { ...opts, body: req }
    );
  }

  /**
   * Customer inquiry.
   *
   * `includeUdf` adds PCB's CUSTOM 800-series CIF fields — the 2nd ID block
   * (801 number / 813 issuer / 814 type / 815 issue dt / 816 exp dt) — which the
   * server fetches via a SECOND x_BusDetail call.
   *
   * OPT-IN on purpose: jXchange returns only ONE extension element per call, so
   * the 2nd ID cannot ride along with the CIP block, and the x_BusDetail payload
   * is ~100 KB vs ~3 KB for the plain read. Ask for it only where a 2nd ID is
   * actually shown (Edit CIF), never on a list or a lookup.
   */
  public custInq(
    cifId: string,
    opts?: RequestOptions,
    includeUdf = false
  ): Promise<DomainResponseEnvelope<CustInqResult>> {
    return this.http.get<DomainResponseEnvelope<CustInqResult>>(
      `/accounts/cust-inq/${encodeURIComponent(cifId)}${includeUdf ? '?includeUdf=true' : ''}`,
      { ...opts }
    );
  }

  public custRelAdd(req: CustRelAddRequest, opts?: RequestOptions): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/cust-rel-add', { ...opts, body: req });
  }

  /**
   * Same route with ?enrich=1: one shaped row per account carrying balance,
   * date opened (F17), account status (F20) and, for ATM rows, card status
   * (F103). Server fans out the per-account inquiries; the client never does.
   */
  public custAcctRelInqEnriched(
    cifId: string,
    opts?: RequestOptions
  ): Promise<CustAcctRelInqEnrichedResult> {
    return this.http.get<CustAcctRelInqEnrichedResult>(
      `/accounts/cust-acct-rel-inq/${encodeURIComponent(cifId)}`,
      { ...opts, query: { enrich: 1 } }
    );
  }

  public custAcctRelInq(
    cifId: string,
    opts?: RequestOptions
  ): Promise<CustAcctRelInqResult> {
    return this.http.get<CustAcctRelInqResult>(
      `/accounts/cust-acct-rel-inq/${encodeURIComponent(cifId)}`,
      { ...opts }
    );
  }

  /**
   * Normalized account party roster (BACKLOG-0794) — every customer related to an
   * account with their capacity (AcctRelCode + label). Account-keyed inverse of
   * custAcctRelInq; account-type-agnostic (loans + deposits). Backed by jXchange
   * AcctRelSrch, normalized server-side. POST /v3/accounts/parties.
   *
   * INTERNAL-FIRST: `taxId` is the real (unmasked) SSN/EIN; prefer `taxIdMasked`
   * for display and mask at the RCP/online-application boundary.
   */
  public parties(
    req: AccountPartiesRequest,
    opts?: RequestOptions
  ): Promise<AccountPartiesResponse> {
    return this.http.post<AccountPartiesResponse>('/v3/accounts/parties', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // ID verification
  // ---------------------------------------------------------------------------

  public idVerifyAdd(req: IdVerifyAddRequest, opts?: RequestOptions): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/id-verify-add', { ...opts, body: req });
  }

  public scanIdDocument(
    req: {
      Image: string;
      MimeType: string;
      // Optional provenance context — threaded into the dbo.DocumentExtract row
      // (BE ScanIdRequest accepts these, case-insensitive). Without them the
      // per-doc extract lands with NULL CartId/PersonRefId/CifId and can't be
      // joined to the cart/person (BACKLOG-0748 follow-on).
      CartId?: string;
      PersonRefId?: string;
      CifId?: string;
      OfficerCode?: string;
      ExpectedDocType?: string;
      SourceFileName?: string;
    },
    opts?: RequestOptions
  ): Promise<ScanIdResult> {
    return this.http.post<ScanIdResult>('/accounts/scan-id-document', { ...opts, body: req });
  }

  /**
   * Azure AI Content Understanding analyze — POST /v3/cu/analyze.
   * CU-into-prod Phase 1 (Bill 2026-05-31): the CU replacement for
   * scanIdDocument on the photo-ID path. The CU response is a SUPERSET of
   * ScanIdResult — its `fields` (ScanIdFields projection) + `rawDocumentType`
   * are byte-identical to DI's wire shape (both camelCase-serialized), so
   * IdExtractionService.doExtract consumes it unchanged. The extra keys
   * (analyzerId / category / pcbIdTypeCode / leaves / stats / extractId) are
   * ignored by the ID consumer; they back the per-doc audit + non-ID paths.
   * Default analyzer is prebuilt-idDocument when AnalyzerId is omitted.
   */
  public cuAnalyze(
    req: {
      AnalyzerId?: string;
      Image: string;
      MimeType: string;
      CartId?: string;
      PersonRefId?: string;
      CifId?: string;
      OfficerCode?: string;
      ExpectedDocType?: string;
      SourceFileName?: string;
    },
    opts?: RequestOptions
  ): Promise<
    ScanIdResult & {
      analyzerId?: string;
      category?: string;
      pcbIdTypeCode?: string;
      leaves?: unknown[];
      stats?: unknown;
      extractId?: string;
    }
  > {
    return this.http.post('/v3/cu/analyze', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // Account lifecycle
  // ---------------------------------------------------------------------------

  public acctIdGen(req: AcctIdGenRequest, opts?: RequestOptions): Promise<AcctIdGenResult> {
    return this.http.post<AcctIdGenResult>('/accounts/acct-id-gen', { ...opts, body: req });
  }

  public acctAdd(req: AcctAddRequest, opts?: RequestOptions): Promise<AcctAddResult> {
    return this.http.post<AcctAddResult>('/accounts/acct-add', { ...opts, body: req });
  }

  public acctInq(
    acctId: string,
    acctType: string,
    opts?: RequestOptions
  ): Promise<AcctInqResult> {
    return this.http.get<AcctInqResult>(
      `/accounts/acct-inq/${encodeURIComponent(acctId)}/${encodeURIComponent(acctType)}`,
      { ...opts }
    );
  }

  public acctBalInq(
    acctId: string,
    acctType: string,
    opts?: RequestOptions
  ): Promise<AcctBalInqResult> {
    return this.http.get<AcctBalInqResult>(
      `/accounts/acct-bal-inq/${encodeURIComponent(acctId)}/${encodeURIComponent(acctType)}`,
      { ...opts }
    );
  }

  /**
   * GET /v3/od/customer-total?cif= — the customer's EXISTING Overdraft Limit
   * summed across every deposit account they own, plus the admin cap
   * (od.customerTotalCap, default $500) and remaining headroom. Used at OD
   * selection to enforce (existing + new) <= cap (WT-BACKLOG D1, Collette).
   * Admin-gated (AdminAuthService) — the http client carries the bearer.
   */
  public odCustomerTotal(cif: string, opts?: RequestOptions): Promise<OdCustomerTotalResponse> {
    return this.http.get<OdCustomerTotalResponse>(
      `/v3/od/customer-total?cif=${encodeURIComponent(cif)}`,
      { ...opts }
    );
  }

  public acctMod(req: AcctModRequest, opts?: RequestOptions): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/acct-mod', { ...opts, body: req });
  }

  public kill(req: AcctKillRequest, opts?: RequestOptions): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/kill', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // Addresses
  // ---------------------------------------------------------------------------

  public addrSrch(req: AddrSrchRequest, opts?: RequestOptions): Promise<AddrSrchResult> {
    return this.http.post<AddrSrchResult>('/accounts/addr-srch', { ...opts, body: req });
  }

  public addrSave(req: AddrSaveRequest, opts?: RequestOptions): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/addr-save', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // Safe deposit
  // ---------------------------------------------------------------------------

  public safedepBoxSrch(
    req: SafeDepBoxSrchRequest,
    opts?: RequestOptions
  ): Promise<SafeDepBoxSrchResult> {
    return this.http.post<SafeDepBoxSrchResult>('/accounts/safedep-box-srch', { ...opts, body: req });
  }

  public safedepVacate(
    req: SafeDepVacateRequest,
    opts?: RequestOptions
  ): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/safedep-vacate', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // NetTeller
  // ---------------------------------------------------------------------------

  public nettellerEnroll(
    req: NetTellerEnrollRequest,
    opts?: RequestOptions
  ): Promise<NetTellerEnrollResult> {
    return this.http.post<NetTellerEnrollResult>('/accounts/netteller-enroll', { ...opts, body: req });
  }

  public nettellerAssociate(
    req: NetTellerAssociateRequest,
    opts?: RequestOptions
  ): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/netteller-associate', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // Debit card
  // ---------------------------------------------------------------------------

  public eftCardAdd(req: EftCardAddRequest, opts?: RequestOptions): Promise<EftCardAddResult> {
    return this.http.post<EftCardAddResult>('/accounts/eft-card-add', { ...opts, body: req });
  }

  public eftCardMod(
    req: EftCardModRequest,
    opts?: RequestOptions
  ): Promise<DomainResponseEnvelope<unknown>> {
    return this.http.post<DomainResponseEnvelope<unknown>>('/accounts/eft-card-mod', { ...opts, body: req });
  }

  /**
   * Current design (EFTCardStyleId) of the card on an account — BACKLOG-0873
   * reissue read-back, so the reprint picker pre-selects the customer's
   * existing design. PAN stays server-side (only masked last-4 returned).
   */
  public cardStyle(
    acctId: string,
    cif: string,
    opts?: RequestOptions
  ): Promise<AccountCardStyleResponse> {
    return this.http.get<AccountCardStyleResponse>(
      `/accounts/${encodeURIComponent(acctId)}/card-style?cif=${encodeURIComponent(cif)}`,
      { ...opts }
    );
  }

  // ---------------------------------------------------------------------------
  // Document request (email / SMS dispatch)
  // ---------------------------------------------------------------------------

  public sendDocumentRequest(
    req: SendDocumentRequest,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>('/accounts/send-document-request', { ...opts, body: req });
  }
}
